// purpose: R2の全sim SE idを、共通134 BPMグリッド上の完成済みテクノ音源へ一対一で変換する。

import type { AudioEngine } from '../AudioEngine';
import type { CombatAudioEngine } from './CombatAudioEngine';
import { SFX_LASER, SFX_PLAYER_SHOT } from './GameplaySfxDirector';

const RUNNING_STATE = 'running';
const EVENT_COUNT = 20;
const SAME_GRID_EPSILON = .001;
const PLAYER_SHOT_BURST_GAP_SECONDS = .42;
const LINK_LASER_BURST_GAP_SECONDS = .24;
const LINK_LASER_FALLBACK_INTERVAL_SECONDS = .1;
const GRID_LEAD_SECONDS = .012;
const IMMEDIATE_CORE_SECONDS = .028;
const LINK_LASER_IGNITION_SECONDS = .04;
const GRID_SLOT_SCALE = 100_000;
const PICKUP_MIN_INTERVAL_SECONDS = .032;

/**
 * idごとの着地点。1=16分、2=8分、4=拍。
 * 高頻度イベントは細かく、構造イベントは拍へ置いて編曲上の役割を固定する。
 */
const GRID_MULTIPLE_BY_EVENT = new Int8Array([
  1, 1, 1, 2, 2,
  1, 1, 2, 2, 1,
  2, 4, 4, 4, 4,
  1, 4, 1, 1, 2,
]);

/** 操作結果を待たせるべきでないイベント。音源自体は同じ編成・調性で制作済み。 */
const IMMEDIATE_EVENT = new Uint8Array(EVENT_COUNT);
IMMEDIATE_EVENT[15] = 1; // player down
IMMEDIATE_EVENT[17] = 1; // purchase accepted
IMMEDIATE_EVENT[18] = 1; // purchase rejected

const PAN_BY_EVENT = new Float32Array([
  .12, .08, -.1, 0, .06,
  -.14, .16, 0, -.08, .08,
  -.08, 0, 0, .06, 0,
  0, .08, .12, -.12, 0,
]);

/** 同一グリッドでは上位イベントを残し、混戦を一つのフィルへ畳む。 */
const PRIORITY_BY_EVENT = new Int8Array([
  25, 10, 35, 55, 65,
  48, 24, 42, 52, 54,
  56, 60, 90, 92, 100,
  100, 82, 30, 32, 58,
]);

/** 操作感をグリッド待ちにしない核。音楽成分とは同じ音源の冒頭を使う。 */
const HAS_IMMEDIATE_CORE = new Uint8Array(EVENT_COUNT);
HAS_IMMEDIATE_CORE[2] = 1; // small kill
HAS_IMMEDIATE_CORE[4] = 1; // large kill

const SHOT_PAN_BY_FORMATION = new Float32Array([.04, -.08, .14, -.14, .08, 0]);
const SHOT_ACCENT_CYCLE_BY_FORMATION = new Int8Array([4, 8, 3, 6, 4, 8]);

interface GridVoice {
  readonly source: AudioBufferSourceNode;
  readonly priority: number;
}

export class TechnoGameplaySfxDirector {
  private readonly lastGridByEvent = new Float64Array(EVENT_COUNT).fill(Number.NEGATIVE_INFINITY);
  private readonly gridVoices = new Map<number, GridVoice>();
  private lastPlayerShotAt = Number.NEGATIVE_INFINITY;
  private lastLinkLaserAt = Number.NEGATIVE_INFINITY;
  private lastLinkLaserFallbackAt = Number.NEGATIVE_INFINITY;
  private lastPickupAt = Number.NEGATIVE_INFINITY;
  private pickupSide = 1;
  private shotIndex = 0;

  constructor(
    private readonly engine: CombatAudioEngine,
    private readonly musicClock: AudioEngine,
  ) {}

  play(id: number, chain: number, formation: number): boolean {
    const context = this.engine.ctx;
    if (!context || context.state !== RUNNING_STATE || id < 0 || id >= EVENT_COUNT) return false;
    if (id === 0) {
      const now = context.currentTime;
      if (now - this.lastPickupAt < PICKUP_MIN_INTERVAL_SECONDS) return true;
      this.lastPickupAt = now;
      this.pickupSide *= -1;
      // Pickup is immediate feedback, not a quantized accent. The former grid
      // arbitration let every kill/laser in the same slot replace it entirely.
      this.engine.playTechnoEvent(id, undefined, this.pickupSide * .16, 1.18);
      return true;
    }
    if (id === SFX_PLAYER_SHOT) {
      this.playerShot(context, formation);
      return true;
    }
    if (id === SFX_LASER) {
      this.linkLaser(context);
      return true;
    }
    if (IMMEDIATE_EVENT[id] !== 0) {
      this.engine.playTechnoEvent(id, undefined, PAN_BY_EVENT[id] ?? 0);
      return true;
    }
    if (HAS_IMMEDIATE_CORE[id] !== 0) {
      this.engine.playTechnoEvent(id, undefined, PAN_BY_EVENT[id] ?? 0, id === 4 ? .42 : .3, IMMEDIATE_CORE_SECONDS);
    }

    const multiple = GRID_MULTIPLE_BY_EVENT[id] || 1;
    const at = this.musicClock.nextR2MusicGridTime(multiple, GRID_LEAD_SECONDS);
    if (at === null) {
      this.engine.playTechnoEvent(id, undefined, PAN_BY_EVENT[id] ?? 0);
      return true;
    }
    if (at <= (this.lastGridByEvent[id] ?? Number.NEGATIVE_INFINITY) + SAME_GRID_EPSILON) return true;
    this.lastGridByEvent[id] = at;
    if (id === 2) {
      const fourKillAccent = chain > 0 && chain % 4 === 0;
      const chainLift = Math.min(.14, Math.max(0, chain - 1) * .009);
      const clapPan = chain & 1 ? -.16 : .16;
      this.scheduleEvent(id, at, clapPan, (fourKillAccent ? 1.18 : .94) + chainLift);
    } else {
      this.scheduleEvent(id, at, PAN_BY_EVENT[id] ?? 0, 1);
    }
    return true;
  }

  /** 初弾は即時、押し続けた列だけを16分へ固定。同一グリッド内の弾は一音へ畳む。 */
  private playerShot(context: AudioContext, formation: number): void {
    const now = context.currentTime;
    const beginsBurst = now - this.lastPlayerShotAt > PLAYER_SHOT_BURST_GAP_SECONDS;
    this.lastPlayerShotAt = now;
    const safeFormation = Math.max(0, Math.min(SHOT_PAN_BY_FORMATION.length - 1, formation | 0));
    const pan = SHOT_PAN_BY_FORMATION[safeFormation] ?? 0;
    if (beginsBurst) {
      this.lastGridByEvent[SFX_PLAYER_SHOT] = Number.NEGATIVE_INFINITY;
      this.shotIndex = 0;
      this.engine.shot(undefined, 1, pan);
      return;
    }
    const at = this.musicClock.nextR2MusicGridTime(1, GRID_LEAD_SECONDS);
    if (at === null) {
      this.engine.shot(undefined, 1, pan);
      return;
    }
    if (at <= this.lastGridByEvent[SFX_PLAYER_SHOT] + SAME_GRID_EPSILON) return;
    this.lastGridByEvent[SFX_PLAYER_SHOT] = at;
    this.shotIndex += 1;
    const accentCycle = SHOT_ACCENT_CYCLE_BY_FORMATION[safeFormation] || 4;
    const barAccent = this.shotIndex % 16 === 0;
    const halfBarAccent = this.shotIndex % 8 === 0;
    const beatAccent = this.shotIndex % 4 === 0;
    const formationAccent = this.shotIndex % accentCycle === 0;
    const level = barAccent ? 1.32
      : halfBarAccent ? 1.24
        : beatAccent ? 1.14
          : formationAccent ? 1.04
            : .9;
    this.scheduleEvent(SFX_PLAYER_SHOT, at, pan, level);
  }

  /**
   * Grade Vは通常射撃音へ重ねない。押し始めだけ短い点火核を即時に返し、
   * 保持中の走査音は同一16分スロットへ一音に畳んでBGMと一体化する。
   */
  private linkLaser(context: AudioContext): void {
    const now = context.currentTime;
    const beginsBurst = now - this.lastLinkLaserAt > LINK_LASER_BURST_GAP_SECONDS;
    this.lastLinkLaserAt = now;
    const pan = PAN_BY_EVENT[SFX_LASER] ?? 0;
    if (beginsBurst) {
      this.lastGridByEvent[SFX_LASER] = Number.NEGATIVE_INFINITY;
      this.lastLinkLaserFallbackAt = now;
      this.engine.playTechnoEvent(
        SFX_LASER,
        undefined,
        pan,
        .46,
        LINK_LASER_IGNITION_SECONDS,
      );
    }
    const at = this.musicClock.nextR2MusicGridTime(1, GRID_LEAD_SECONDS);
    if (at === null) {
      if (
        !beginsBurst
        && now - this.lastLinkLaserFallbackAt >= LINK_LASER_FALLBACK_INTERVAL_SECONDS
      ) {
        this.lastLinkLaserFallbackAt = now;
        this.engine.playTechnoEvent(SFX_LASER, undefined, pan, .88);
      }
      return;
    }
    if (at <= this.lastGridByEvent[SFX_LASER] + SAME_GRID_EPSILON) return;
    this.lastGridByEvent[SFX_LASER] = at;
    this.scheduleEvent(SFX_LASER, at, pan, beginsBurst ? 1.08 : .88);
  }

  private scheduleEvent(id: number, at: number, pan: number, levelScale: number): void {
    const slot = Math.round(at * GRID_SLOT_SCALE);
    const priority = PRIORITY_BY_EVENT[id] ?? 0;
    const current = this.gridVoices.get(slot);
    if (current && current.priority >= priority) return;
    if (current) {
      try {
        current.source.stop();
      } catch {
        // already ended: the replacement can still become the audible punctuation
      }
    }
    const source = this.engine.playTechnoEvent(id, at, pan, levelScale);
    if (!source) return;
    this.gridVoices.set(slot, { source, priority });
    source.addEventListener('ended', () => {
      if (this.gridVoices.get(slot)?.source === source) this.gridVoices.delete(slot);
    }, { once: true });
  }
}
